import { useContext, useLayoutEffect } from 'react'

import axios from 'axios'

import { AuthContext } from '~/contexts/AuthContext'

const WithRequest = ({ children }) => {
  axios.defaults.headers.post['Content-Type'] = 'application/json'

  const { authToken, refreshAuthToken } = useContext(AuthContext)

  useLayoutEffect(() => {
    /*
      Adds an auth token to any requests which do not yet hold one.
      Doesn't add to requests with tokens, as authToken can be stale
      if the request is triggered immediately after a token refresh.
    */
    const reqInterceptor = axios.interceptors.request.use(
      (config) => {
        if (!config.headers?.authorization) {
          config.headers['authorization'] = `Bearer ${authToken?.token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    /*
      Intercepts any 401 (unauthorized) responses and retries the
      original request with a refreshed token.
    */
    const resInterceptor = axios.interceptors.response.use(
      (response) => {
        return response
      },
      (error) => {
        const originalRequest = error.config
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true
          return refreshAuthToken().then((res) => {
            originalRequest.headers = {
              authorization: `Bearer ${res?.token}`,
            }
            return axios(originalRequest)
          })
        }
        return Promise.reject(error)
      }
    )

    return () => {
      /*
        Axios creates a new interceptor for each new token. This removes any previous interceptors.
      */
      axios.interceptors.request.eject(reqInterceptor)
      axios.interceptors.response.eject(resInterceptor)
    }
  }, [authToken])

  return children
}

export default WithRequest
